import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import Footer from './Footer';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './Common.css';
import './CulturalInsightsChallenge.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/lb-api';

const CulturalInsightsChallenge = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language = 'de', proficiencyLevel = 'beginner-a1' } = location.state || {};
  const [selectedCountry, setSelectedCountry] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [countries, setCountries] = useState([]);
  const [confirmed, setConfirmed] = useState(false);

  usePageTimeTracker('cultural-insights-challenge', user);

  // Fetch countries from DB
  useEffect(() => {
    axios.get(`${API_BASE_URL}/cultural-insights/countries`, { withCredentials: true })
      .then(res => {
        if (res.data.success) setCountries(res.data.countries);
      })
      .catch(err => {
        console.error('Failed to fetch countries:', err);
        setCountries([
          { country_id: 1, country_name: 'Germany', country_code: 'de', flag_url: '/images/germany_flag.png' },
          { country_id: 2, country_name: 'France', country_code: 'fr', flag_url: '/images/france_flag.png' },
          { country_id: 3, country_name: 'Spain', country_code: 'es', flag_url: '/images/spain_flag.png' },
          { country_id: 4, country_name: 'Italy', country_code: 'it', flag_url: '/images/italy_flag.png' },
          { country_id: 5, country_name: 'Japan', country_code: 'ja', flag_url: '/images/japan_flag.png' }
        ]);
      });
  }, []);

  const breadcrumbs = [
    { label: 'Home', path: '/', current: false },
    { label: 'Explore Learning', path: '/explore-learning', current: false },
    { label: 'Cultural Insight Challenge', current: true }
  ];

  const handleStart = () => {
    if (!selectedCountry) return;
    // Clear ALL previous challenge sessions from sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('ci_challenge_') || key.startsWith('ci_quiz_data_')) sessionStorage.removeItem(key);
    });
    const countryData = countries.find(c => c.country_name === selectedCountry);
    navigate('/cultural-insights-challenge-quiz', { state: { language, proficiencyLevel, country: selectedCountry, countryFlag: countryData?.flag_url || '' } });
  };

  const getCountryFlag = (name) => {
    const country = countries.find(c => c.country_name === name);
    return country?.flag_url || '/images/flag-en.svg';
  };

  return (
    <div className="ci-challenge-page">
      <Header user={user} />
      <div className="nav-header-container">
        <SideNav />
        <PageHeader
          breadcrumbs={breadcrumbs}
          language={language}
          proficiencyLevel={proficiencyLevel}
          rightContent={<div />}
        />
      </div>

      <main className="ci-challenge-main">
        <div className="ci-challenge-content">
          <h1 className="ci-challenge-title">
            Cultural <span className="ci-challenge-title-highlight">Insight</span>{' '}
            <span className="ci-challenge-title-italic">Challenge</span>
          </h1>
          <p className="ci-challenge-description">
            Explore your cultural awareness through real-world scenarios and multiple-choice questions while discovering five key cultural dimensions: Communication Style, Workplace Culture, Social Etiquette, Traditions and Celebrations, and Civic Behaviour.
          </p>

          <div className="ci-challenge-before">
            <span className="ci-challenge-before-text">Before you begin</span>
          </div>

          <div className="ci-challenge-card">
            <div className="ci-challenge-card-header">
              <span className="ci-challenge-card-header-text">Choose your country</span>
              <p className="ci-challenge-card-subtext">Your challenge will be tailored to the culture and context of the country you select.</p>
            </div>

            <div className="ci-challenge-dropdown-wrapper">
              <button className="ci-challenge-dropdown" onClick={() => setShowDropdown(!showDropdown)}>
                {selectedCountry && (
                  <>
                    <span style={{ color: '#000', fontFamily: 'Graphik-Semibold' }}>{selectedCountry}</span>
                    <span style={{ color: '#CFCFCF', margin: '0 10px' }}>|</span>
                  </>
                )}
                <span style={{ color: '#A100FF', fontFamily: 'Graphik-Semibold' }}>
                  Change Country
                </span>
                <img src="/images/purplearrow.svg" alt="" width="18" height="18" style={{ transform: showDropdown ? 'rotate(180deg)' : 'none' }} />
              </button>
              {showDropdown && (
                <div className="ci-challenge-dropdown-list">
                  {countries.map(c => (
                    <button key={c.country_id} className="ci-challenge-dropdown-item" onClick={() => { setSelectedCountry(c.country_name); setShowDropdown(false); }}>
                      <img src={c.flag_url} alt="" width="20" height="14" style={{ marginRight: '8px' }} />
                      {c.country_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="ci-challenge-divider"></div>

            <div className="ci-challenge-tips">
              <h4 className="ci-challenge-tips-title">To get the most out of your results</h4>
              <ul className="ci-challenge-tips-list">
                <li>Attempt at least 2 questions per category — it gives your score more depth and balance.</li>
                <li>Complete at least 2 case studies to unlock your full cultural insight report.</li>
              </ul>
            </div>

            <label className="ci-challenge-checkbox-label">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
              <span className="ci-challenge-checkbox-custom"></span>
              <span>I understand my country selection cannot be changed once the challenge begins</span>
            </label>
          </div>

          <button className={`ci-challenge-start-btn ${selectedCountry && confirmed ? 'active' : ''}`} onClick={handleStart} disabled={!selectedCountry || !confirmed}>
            I am ready, Let's go!
            <img src="/images/arrow-right.svg" alt="" width="18" height="18" className="arrow-icon" />
          </button>

          <p className="ci-challenge-footer-note">Take your time and trust your instincts.</p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CulturalInsightsChallenge;
